import AppData from 'Data/AppData';
import { useAuthContext } from 'Data/context/Auth';
import { OBJECT_STATUS } from 'Data/Enum';
import UserData from 'Data/UserData';
import DataManager from 'Lib/DataManager';
import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useState } from 'react';
import { getNameWithInactiveTag } from 'Util';

const userMap = new Map();

export const getUserNameFromId = (userId) => {
  return (userId && userMap.get(userId)?.displayName) || '';
};

const UserContext = createContext({
  fullUserList: [],
  getFilteredList: () => [],
});
UserContext.displayName = 'UserContext';

let DM = new DataManager();
export const UserStateProvider = ({ children }) => {
  const { isLoggedIn } = useAuthContext();
  const [fullUserList, setFullUserList] = useState([]);

  useEffect(() => {
    DM.add(
      UserData.registerListener('ALL_USERS_LOADED', onListLoaded),
      UserData,
    );
    DM.add(
      UserData.registerListener('RECORD_UPDATED', updateMapForUser),
      UserData,
    );
    DM.add(
      UserData.registerListener('RECORD_ACTIVATED', updateMapForUser),
      UserData,
    );
    DM.add(
      UserData.registerListener('RECORD_DEACTIVATED', updateMapForUser),
      UserData,
    );
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      UserData.populateUsers();
    } else {
      setFullUserList([]);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (fullUserList) {
      userMap.clear();
      fullUserList.forEach((user) => {
        userMap.set(user.id, {
          full_name: user.full_name,
          displayName: user.displayName,
          status: user.status,
          username: user.username,
        });
      });
    }
  }, [fullUserList]);

  const onListLoaded = (list) => {
    setFullUserList(
      list.map((item) => {
        return {
          ...item,
          displayName: getNameWithInactiveTag(item, 'full_name'),
        };
      }),
    );
  };
  const updateMapForUser = (updatedUser) => {
    userMap.set(updatedUser.id, {
      full_name: updatedUser.full_name,
      displayName: getNameWithInactiveTag(updatedUser, 'full_name'),
      status: updatedUser.status,
      username: updatedUser.username,
    });
  };

  const getFilteredList = ({
    useFullList,
    userList,
    includedId,
    excludedIds,
    showDeactivated = false,
  }) => {
    let list =
      useFullList || !userList
        ? [...userMap.entries()].map(([key, value]) => {
            return { id: key, ...value };
          })
        : userList;

    let includedUser =
      !list.find((user) => user.id === includedId) && userMap.get(includedId);

    let sortedList = [...(includedUser ? [includedUser] : []), ...list].filter(
      (user) =>
        (!excludedIds?.length || !excludedIds.includes(user.id)) &&
        (showDeactivated || user.status === OBJECT_STATUS.ACTIVE.key),
    );

    if (AppData.userID()) {
      let currentUser = sortedList.find((user) => user.id === AppData.userID());
      if (currentUser) {
        sortedList = [
          {
            ...currentUser,
            displayName: currentUser.displayName?.endsWith('(me)')
              ? currentUser.displayName
              : (currentUser.displayName += ' (me)'),
          },
          ...sortedList.filter((user) => user.id !== currentUser.id),
        ];
      }
    }

    return sortedList;
  };

  return (
    <UserContext.Provider
      value={{
        fullUserList,
        getFilteredList,
      }}>
      {children}
    </UserContext.Provider>
  );
};

UserStateProvider.propTypes = {
  children: PropTypes.node,
};

export const useUserContext = () => useContext(UserContext);
